import { readonly, ref, type Ref } from "vue";

/**
 * How much of the window the soft keyboard covers, in CSS px.
 *
 * Android's WebView shrinks the *visual* viewport for the IME but leaves the
 * *layout* viewport alone, so `height: 100%` chains and `position: absolute`
 * children never move on their own. `interactive-widget=resizes-content` in the
 * viewport meta is supposed to shrink the layout viewport instead, but it isn't
 * honoured under the edge-to-edge Android shell — hence this measurement.
 *
 * It self-cancels: if the layout viewport ever does start shrinking, then
 * `window.innerHeight` shrinks along with `visualViewport.height` and this
 * lands back on 0, so nothing gets lifted twice.
 *
 * Module-level singleton (same shape as `useUiZoom`) so any surface can read it
 * without prop drilling. The value is also published as `--zw-keyboard-inset` on
 * `<html>`, which is what lets CSS in teleported dialogs and menus see it —
 * custom properties inherit down the DOM tree, and a teleported panel is a child
 * of `<body>`, not of whichever component rendered it.
 */
const insetPx = ref(0);
const CSS_VAR = "--zw-keyboard-inset";
/** Below this, it's browser chrome jitter rather than a keyboard. */
const JITTER_PX = 24;

function publish(value: number): void {
  insetPx.value = value;
  document.documentElement.style.setProperty(CSS_VAR, `${value}px`);
}

function measure(): void {
  const vv = window.visualViewport;
  if (vv === null) {
    publish(0);
    return;
  }
  const covered = window.innerHeight - vv.height - vv.offsetTop;
  publish(covered > JITTER_PX ? Math.round(covered) : 0);
}

/**
 * Start tracking. Call once, from the app root — every surface reads the same
 * singleton. Returns a teardown.
 */
export function installKeyboardInset(): () => void {
  const vv = window.visualViewport;
  measure();
  vv?.addEventListener("resize", measure);
  vv?.addEventListener("scroll", measure);
  window.addEventListener("resize", measure);

  return () => {
    vv?.removeEventListener("resize", measure);
    vv?.removeEventListener("scroll", measure);
    window.removeEventListener("resize", measure);
    publish(0);
  };
}

/** Read-only access for components that need the value in JS as well as CSS. */
export function useKeyboardInset(): {
  keyboardInsetPx: Readonly<Ref<number, number>>;
} {
  return { keyboardInsetPx: readonly(insetPx) };
}
